import {
  STORE_KEY_ID_LIST,
  evalCondition,
  storeGet,
} from "mailup-react-helpers";

import type {
  ActivityEmailEventType,
  GetEmailRecipientTrackingMostRecentChangeLogResponse,
  GetEmailRecipientTrackingChangeLogsQsParams,
  SubscriptionSpecOptOutType,
} from "Types/beRecipientsApi";

import {
  beRecipientGetRecipient,
  beRecipientUpdateRecipient,
} from "Api/beRecipientApi";

import {
  beRecipientApiGetEmailRecipientTracking,
  beRecipientApiGetEmailRecipientTrackingMostRecentChangeLog,
  beRecipientApiGetEmailRecipientTrackingChangeLogs,
  beRecipientApiOptinEmailRecipientFromList,
  beRecipientApiOptinSmsRecipientFromList,
  beRecipientApiOptoutEmailRecipientFromList,
  beRecipientApiOptoutSmsRecipientFromList,
  beRecipientApiPendingEmailRecipientFromList,
  beRecipientApiPendingSmsRecipientFromList,
  beRecipientApiSetEmailRecipientTracking,
  beRecipientGetSmsSubscriptionFromList,
  beRecipientGetSubscriptionEmailFromList,
} from "Api/beRecipientApiTS";

import type {
  ActivityStreamFilterType,
  ChannelSubscriptionStatus,
  DynamicField,
  OptoutConditions,
  PanelEditRecipientState,
  RecipientTrackingChangeLogsState,
  ResultField,
  SearchResult,
} from "Pages/RecipientsList/model";

import {
  EMAIL_OPTOUT_TYPE_BLOCKEDEMAIL,
  EMAIL_OPTOUT_TYPE_BOUNCE,
  EMAIL_OPTOUT_TYPE_CONSOLE,
  EMAIL_OPTOUT_TYPE_CONTINUALBOUNCES,
  EMAIL_OPTOUT_TYPE_FEEDBACKLOOP,
  EMAIL_OPTOUT_TYPE_IMPORT,
  EMAIL_OPTOUT_TYPE_LISTUNSUBSCRIBEMAILTO,
  EMAIL_OPTOUT_TYPE_LISTUNSUBSCRIBEPAGE,
  EMAIL_OPTOUT_TYPE_RECIPIENTINACTIVE,
  EMAIL_OPTOUT_TYPE_RIGHTTOBEFORGOTTEN,
  EMAIL_OPTOUT_TYPE_UNSUBSCRIBE,
} from "Constants";

import { toDateUI } from "./results";

export const toSubscriptionStatus = (
  status: number | undefined,
): "SUBSCRIBED" | "PENDING" | "UNSUBSCRIBED" | undefined => {
  return status === 0
    ? "SUBSCRIBED"
    : status === 1
      ? "PENDING"
      : status === 2
        ? "UNSUBSCRIBED"
        : undefined;
};

export const toOptOutType = (
  optoutType: number,
  optoutSubType?: number,
): SubscriptionSpecOptOutType | undefined => {
  switch (optoutType) {
    case EMAIL_OPTOUT_TYPE_CONSOLE:
      return "OptOutPlatform";
    case EMAIL_OPTOUT_TYPE_UNSUBSCRIBE:
      return "OptOutNewsletterLink";
    case EMAIL_OPTOUT_TYPE_IMPORT:
      return "OptOutImport";
    case EMAIL_OPTOUT_TYPE_BOUNCE:
      switch (optoutSubType) {
        case 1:
          return "OptOutBounceMailErrata";
        case 2:
          return "OptOutBounceDominioErrato";
        case 3:
          return "OptOutBounceCasellaAbbandonata";
      }
      return "OptOutBounceHB";

    case EMAIL_OPTOUT_TYPE_FEEDBACKLOOP:
      return "OptOutFBL";
    case EMAIL_OPTOUT_TYPE_LISTUNSUBSCRIBEPAGE:
      return "OptOutUnsubHttp";
    case EMAIL_OPTOUT_TYPE_LISTUNSUBSCRIBEMAILTO:
      return "OptOutUnsubMailTo";
    case EMAIL_OPTOUT_TYPE_RECIPIENTINACTIVE:
      return "OptOutInactive";

    case EMAIL_OPTOUT_TYPE_BLOCKEDEMAIL:
      switch (optoutSubType) {
        case 1:
          return "OptOutMailBlockedMta";
        case 2:
          return "OptOutMailBlockedSpam";
        case 3:
          return "OptOutMailBlockedConfigMta";
      }

      return "OptOutMailBlocked";

    case EMAIL_OPTOUT_TYPE_CONTINUALBOUNCES:
      switch (optoutSubType) {
        case 1:
          return "OptOutNBHProvisional";
        case 2:
          return "OptOutNBHDefinitive";
      }

      return "OptOutNBH";

    case EMAIL_OPTOUT_TYPE_RIGHTTOBEFORGOTTEN:
      return "OptOutRightToBeForgotten";
  }

  return undefined;
};

export const fetchRecipient = async (
  idRecipient: number,
  dynamicFields: DynamicField[],
): Promise<SearchResult> => {
  const idList = storeGet(STORE_KEY_ID_LIST);

  const hasAdvRecMgmt: boolean = evalCondition("co:ADVRECIPIENTMGMT");

  const [
    { Id, Email, MobilePrefix, MobileNumber, Fields, RegistrationDate },
    emailSubscription,
    smsSubscription,
  ] = await Promise.all([
    beRecipientGetRecipient(idRecipient),
    beRecipientGetSubscriptionEmailFromList(idRecipient, idList),
    beRecipientGetSmsSubscriptionFromList(idRecipient, idList),
  ]);

  const email = typeof Email === "string" && Email.length > 0 ? Email : "";

  const tmpMobile = `${typeof MobilePrefix === "string" ? MobilePrefix : ""} ${
    typeof MobileNumber === "string" ? MobileNumber : ""
  }`;

  const mobile = tmpMobile.length > 1 ? tmpMobile : "";

  const allFields: ResultField[] = dynamicFields.map(({ id }) => ({
    id,
    value: Fields.find((f: { Id: number; Value: string }) => f.Id === id)
      ?.Value,
  }));

  const EmailSubscriptionStatus = toSubscriptionStatus(
    emailSubscription?.Status,
  );

  const optoutConditions: OptoutConditions | undefined =
    emailSubscription?.OptoutConditions?.Condition === "OPTCOND_COUNTSEND"
      ? {
          Condition: "OPTCOND_COUNTSEND",
          CountSend: emailSubscription.OptoutConditions.CountSend as number,
        }
      : emailSubscription?.OptoutConditions?.Condition ===
          "OPTCOND_SENDINTERVAL"
        ? {
            Condition: "OPTCOND_SENDINTERVAL",
            SendInterval: emailSubscription.OptoutConditions
              .SendInterval as number,
          }
        : emailSubscription?.OptoutConditions?.Condition ===
            "OPTCOND_DIFFERENTEMAIL"
          ? {
              Condition: "OPTCOND_DIFFERENTEMAIL",
              DifferentEmail: emailSubscription.OptoutConditions
                .DifferentEmail as string,
            }
          : emailSubscription?.OptoutConditions?.Condition ===
              "OPTCOND_DATERANGE"
            ? {
                Condition: "OPTCOND_DATERANGE",
                DateFrom: toDateUI(
                  emailSubscription.OptoutConditions.DateFrom,
                  "YYYY-MM-DD HH:mm",
                ) as string,
                DateTo: toDateUI(
                  emailSubscription.OptoutConditions.DateTo,
                  "YYYY-MM-DD HH:mm",
                ) as string,
              }
            : undefined;

  return {
    Id,
    Email: email,
    Mobile: mobile,
    MobilePrefix,
    MobileNumber,
    Fields: allFields,
    EmailSubscriptionStatus,
    RegistrationDate: toDateUI(RegistrationDate),
    EmailSubscriptionDate: toDateUI(emailSubscription?.SubscriptionDate),
    EmailSubscriptionDateExt: toDateUI(
      emailSubscription?.SubscriptionDate,
      "YYYY-MM-DD HH:mm",
    ),
    EmailPendingDate: toDateUI(emailSubscription?.SubscriptionRequestDate),
    EmailPendingDateExt: toDateUI(
      emailSubscription?.SubscriptionRequestDate,
      "YYYY-MM-DD HH:mm",
    ),
    EmailPendingDateHMS: toDateUI(
      emailSubscription?.SubscriptionRequestDate,
      "YYYY-MM-DD HH:mm:ss",
    ),
    EmailUnsubscriptionDate: toDateUI(emailSubscription?.OptOutDate),
    EmailUnsubscriptionDateExt: toDateUI(
      emailSubscription?.OptOutDate,
      "YYYY-MM-DD HH:mm:ss",
    ),
    EmailConfirmationDateExt: toDateUI(
      emailSubscription?.ConfirmationDate,
      "YYYY-MM-DD HH:mm",
    ),
    EmailConfirmationIP: emailSubscription?.ConfirmationIP,
    EmailSubscriptionRequestIP: emailSubscription?.SubscriptionRequestIP,
    ConfirmedOptin: emailSubscription?.ConfirmedOptin,
    SMSSubscriptionStatus: toSubscriptionStatus(smsSubscription?.Status),
    SMSSubscriptionDate: toDateUI(smsSubscription?.SubscriptionDate),
    SMSSubscriptionDateExt: toDateUI(
      smsSubscription?.SubscriptionDate,
      "YYYY-MM-DD HH:mm",
    ),
    SMSUnsubscriptionDate: toDateUI(smsSubscription?.OptOutDate),
    SMSUnsubscriptionDateExt: toDateUI(
      smsSubscription?.OptOutDate,
      "YYYY-MM-DD HH:mm",
    ),
    ReasonEmail: emailSubscription?.OptoutReason ?? undefined,
    ReasonSms: smsSubscription?.OptoutReason ?? undefined,
    SMSConfirmationIP: smsSubscription?.ConfirmationIP,
    SMSSubscriptionRequestIP: smsSubscription?.SubscriptionRequestIP,

    optoutConditions,

    actions: {
      profile: false,
      edit: false,
      unsubscribe: false,
      delete: false,
      sendConfirmationEmail:
        hasAdvRecMgmt && EmailSubscriptionStatus === "PENDING",
      canChangeEmailStatus:
        emailSubscription !== null && emailSubscription !== undefined,
      canChangeSmsStatus:
        smsSubscription !== null && smsSubscription !== undefined,
    },
  };
};

export const isFieldConfidential = (
  idField: number,
  dynamicFields: DynamicField[],
): boolean => {
  return dynamicFields.some((df) => df.id === idField && df.masked);
};

export const updateRecipientField = async (
  idRecipient: number,
  idField: number,
  value: string,
  dynamicFields: DynamicField[],
): Promise<void> => {
  if (isFieldConfidential(idField, dynamicFields)) {
    return;
  }

  const { Fields } = await beRecipientGetRecipient(idRecipient);

  const prevFields = Fields.filter(
    (f: { Id: number }) => !isFieldConfidential(f.Id, dynamicFields),
  ).filter((f: { Id: number }) => f.Id !== idField);

  const updateData = {
    Fields: [...prevFields, { Id: idField, Value: value }],
  };

  await beRecipientUpdateRecipient(idRecipient, updateData);
};

export const panelEditRecipientUpdater =
  (fieldId: number, fieldValue: string) =>
  (state: PanelEditRecipientState): PanelEditRecipientState => {
    if (state.recipient !== undefined) {
      return {
        ...state,
        recipient: {
          ...state.recipient,
          Fields: state.recipient.Fields.map((df) =>
            df.id === fieldId ? { ...df, value: fieldValue } : df,
          ),
        },
      };
    }

    return state;
  };

export const resultsListUpdater =
  (idRecipient: number, fieldId: number, fieldValue: string) =>
  (state: SearchResult[]): SearchResult[] => {
    return state.map((item) =>
      item.Id === idRecipient
        ? {
            ...item,
            Fields: [
              ...item.Fields.filter((f) => f.id !== fieldId),
              {
                id: fieldId,
                value: fieldValue,
              },
            ],
          }
        : item,
    );
  };

export const resultsListRecipientsUpdater =
  (recipient: SearchResult) =>
  (state: SearchResult[]): SearchResult[] => {
    //  merge (not replace) so list-only fields untouched by fetchRecipient (e.g. TrackingState) survive
    return state.map((item) =>
      item.Id === recipient.Id ? { ...item, ...recipient } : item,
    );
  };

export const getOptoutText = (
  intl: any,
  t: SubscriptionSpecOptOutType,
): string | undefined => {
  switch (t) {
    case "OptOutPlatform":
      return intl.formatMessage({
        id: "Load.SearchRecipientsUnsubscribed.SelectCauseAdminAction",
      });

    case "OptOutNewsletterLink":
      return intl.formatMessage({
        id: "Load.SearchRecipientsUnsubscribed.SelectCauseUnsubbcribeLink",
      });

    case "OptOutImport":
      return intl.formatMessage({
        id: "Load.SearchRecipientsUnsubscribed.SelectCauseFromImport",
      });
    case "OptOutBounceHB":
      return intl.formatMessage({
        id: "Load.SearchRecipientsUnsubscribed.SelectCauseEmailErrata",
      });
    case "OptOutBounceMailErrata":
      return intl.formatMessage({
        id: "Load.SearchRecipientsUnsubscribed.SelectCauseEmailErrataSintassi",
      });
    case "OptOutBounceDominioErrato":
      return intl.formatMessage({
        id: "Load.SearchRecipientsUnsubscribed.SelectCauseEmailErrataDominio",
      });
    case "OptOutBounceCasellaAbbandonata":
      return intl.formatMessage({
        id: "Load.SearchRecipientsUnsubscribed.SelectCauseEmailErrataCasellaAbbandonata",
      });
    case "OptOutFBL":
      return intl.formatMessage({
        id: "Load.SearchRecipientsUnsubscribed.SelectCauseSegnalazioneAbuso",
      });
    case "OptOutUnsubHttp":
      return intl.formatMessage({
        id: "Load.SearchRecipientsUnsubscribed.SelectCauseLinkClientPosta",
      });
    case "OptOutUnsubMailTo":
      return intl.formatMessage({
        id: "Load.SearchRecipientsUnsubscribed.SelectCauseMailtoClientPosta",
      });
    case "OptOutInactive":
      return intl.formatMessage({
        id: "Load.SearchRecipientsUnsubscribed.SelectCauseDestinatarioInattivo",
      });
    case "OptOutMailBlocked":
      return intl.formatMessage({
        id: "Load.SearchRecipientsUnsubscribed.SelectCauseEmailBloccata",
      });
    case "OptOutMailBlockedMta":
      return intl.formatMessage({
        id: "Load.SearchRecipientsUnsubscribed.SelectCauseEmailBloccataBloccoMTA",
      });
    case "OptOutMailBlockedSpam":
      return intl.formatMessage({
        id: "Load.SearchRecipientsUnsubscribed.SelectCauseEmailBloccataSpam",
      });
    case "OptOutMailBlockedConfigMta":
      return intl.formatMessage({
        id: "Load.SearchRecipientsUnsubscribed.SelectCauseEmailBloccataConfigurazioneMTA",
      });
    case "OptOutNBH":
      return intl.formatMessage({
        id: "Load.SearchRecipientsUnsubscribed.SelectCauseErroriRipetuti",
      });
    case "OptOutNBHProvisional":
      return intl.formatMessage({
        id: "Load.SearchRecipientsUnsubscribed.SelectCauseDisiscTemporanea",
      });
    case "OptOutNBHDefinitive":
      return intl.formatMessage({
        id: "Load.SearchRecipientsUnsubscribed.SelectCauseDisiscPermanenete",
      });
    case "OptOutRightToBeForgotten":
      return intl.formatMessage({
        id: "Load.SearchRecipientsUnsubscribed.SelectCauseRightToBeForgotten",
      });
  }
};

export const getBounceText = (intl: any, bounceType: any): string | null => {
  switch (bounceType) {
    case "GB":
      return intl.formatMessage({ id: "Load.Bounce.GBText" });

    case "HB":
      return intl.formatMessage({ id: "Load.Bounce.HBText" });

    case "MB":
      return intl.formatMessage({ id: "Load.Bounce.MBText" });

    case "MBAD":
      return intl.formatMessage({ id: "Load.Bounce.MBADText" });

    case "MBKS":
      return intl.formatMessage({ id: "Load.Bounce.MBKSText" });

    case "MBRD":
      return intl.formatMessage({ id: "Load.Bounce.MBRDText" });

    case "MBSD":
      return intl.formatMessage({ id: "Load.Bounce.MBSDText" });

    case "SB":
      return intl.formatMessage({ id: "Load.Bounce.SBText" });

    case "SBDF":
      return intl.formatMessage({ id: "Load.Bounce.SBDFText" });

    case "SBMF":
      return intl.formatMessage({ id: "Load.Bounce.SBMFText" });

    case "TB":
      return intl.formatMessage({ id: "Load.Bounce.TBText" });
  }

  return null;
};

interface updateRecipientSubscriptionStatusExtraParams {
  idConfirmationMessage?: number;
}

export const updateRecipientSubscriptionStatus = async (
  idRecipient: number,
  channel: "EMAIL" | "SMS",
  status: ChannelSubscriptionStatus,
  { idConfirmationMessage }: updateRecipientSubscriptionStatusExtraParams = {},
): Promise<boolean | undefined> => {
  if (status !== undefined) {
    const idList = storeGet(STORE_KEY_ID_LIST);

    const webhookSource = "CONSOLE";

    const r = await (channel === "EMAIL"
      ? status === "SUBSCRIBED"
        ? beRecipientApiOptinEmailRecipientFromList(
            idRecipient,
            idList,
            { webhookSource },
            {
              SubscriptionRequestIP: "",
              ConfirmationIP: "",
              ConfirmedOptin: false,
            },
          )
        : status === "PENDING"
          ? beRecipientApiPendingEmailRecipientFromList(
              idRecipient,
              idList,
              {
                webhookSource,
              },
              {
                IdMsg: idConfirmationMessage,
              },
            )
          : status === "UNSUBSCRIBED"
            ? beRecipientApiOptoutEmailRecipientFromList(
                idRecipient,
                idList,
                {
                  webhookSource,
                },
                {
                  OptOutType: "Platform",
                },
              )
            : undefined
      : status === "SUBSCRIBED"
        ? beRecipientApiOptinSmsRecipientFromList(
            idRecipient,
            idList,
            { webhookSource },
            {
              SubscriptionRequestIP: "",
              ConfirmationIP: "",
              ConfirmedOptin: false,
            },
          )
        : status === "PENDING"
          ? beRecipientApiPendingSmsRecipientFromList(idRecipient, idList, {
              SubscriptionRequestIP: "",
            })
          : status === "UNSUBSCRIBED"
            ? beRecipientApiOptoutSmsRecipientFromList(
                idRecipient,
                idList,
                { webhookSource },
                {
                  OptOutType: "Platform",
                },
              )
            : undefined);

    return r;
  }

  return undefined;
};

export const canChangeEmailSubscriptionStatus = (r: SearchResult): boolean =>
  r.actions.canChangeEmailStatus;

export const canChangeSmsSubscriptionStatus = (r: SearchResult): boolean =>
  r.actions.canChangeSmsStatus;

export const fetchRecipientContact = async (
  idRecipient: number,
): Promise<string> => {
  const { Email, MobilePrefix, MobileNumber } =
    await beRecipientGetRecipient(idRecipient);

  const email = typeof Email === "string" ? Email : "";

  const mobile = `${typeof MobilePrefix === "string" ? MobilePrefix : ""}${
    typeof MobileNumber === "string" ? MobileNumber : ""
  }`;

  return email.length > 0 ? email : mobile;
};

export const getEmailRecipientTrackingInsertDate = async (
  idRecipient: number,
): Promise<{ InsertDate: string; IdNLFrom: number } | undefined> => {
  const idList = storeGet(STORE_KEY_ID_LIST);
  const resp = await beRecipientApiGetEmailRecipientTrackingMostRecentChangeLog(
    idRecipient,
    idList,
  );
  return resp.MostRecentChangeLog
    ? {
        InsertDate:
          toDateUI(resp.MostRecentChangeLog.InsertDate, "YYYY-MM-DD HH:mm") ||
          "",
        IdNLFrom: resp.MostRecentChangeLog.IdNLFrom,
      }
    : undefined;
};

export const activityFiltersToEventTypes = (
  allFilters: boolean,
  f: ActivityStreamFilterType[],
  channel: "email" | "sms",
): ActivityEmailEventType[] => {
  return allFilters
    ? channel === "email"
      ? // all email filters
        [
          "Subscription",
          "SubscriptionRequest",
          "SubscriptionConfirmation",
          "Unsubscription",
          "Bounces",
          "Clicks",
          "Sendings",
          "Opens",
        ]
      : //  all sms filters
        ["Subscription", "Unsubscription", "Clicks", "Sendings"]
    : ([
        ...(f.includes("subscription")
          ? channel === "email"
            ? [
                "Subscription",
                "SubscriptionRequest",
                "SubscriptionConfirmation",
              ]
            : ["Subscription"]
          : []),
        ...(f.includes("unsubscription") ? ["Unsubscription"] : []),
        ...(f.includes("bounces") && channel === "email" ? ["Bounces"] : []),
        ...(f.includes("clicks") ? ["Clicks"] : []),
        ...(f.includes("sendings") ? ["Sendings"] : []),
        ...(f.includes("opens") && channel === "email" ? ["Opens"] : []),
      ] as ActivityEmailEventType[]);
};

export const fetchRecipientTracking = async (
  idRecipient: number,
): Promise<boolean> => {
  const idList = storeGet(STORE_KEY_ID_LIST);
  const {
    Tracking: { Enable },
  } = await beRecipientApiGetEmailRecipientTracking(idRecipient, idList);

  return Enable;
};

export const updateRecipientTracking = async (
  idRecipient: number,
  tracking: boolean,
): Promise<void> => {
  const idList = storeGet(STORE_KEY_ID_LIST);

  await beRecipientApiSetEmailRecipientTracking(idRecipient, idList, {
    Enable: tracking,
    IdMessage: 0,
    RemoteIP: "",
  });

  //TODO
};

/*
  
  
export const recipientActivityStreamActivityTypesState = selectorFamily<
  ActivityEmailEventType[],
  'email' | 'sms'
>({
  key: 'recipientsList_recipientActivityStreamActivityTypesState',
  get:
    (channel: 'email' | 'sms') =>
    ({ get }) => {
      const f = get(recipientActivityStreamFiltersState)

      console.log('### inside f', f)

      return [
        ...(f.includes('subscription')
          ? channel === 'email'
            ? [
                'Subscription',
                'SubscriptionRequest',
                'SubscriptionConfirmation'
              ]
            : ['Subscription']
          : []),
        ...(f.includes('unsubscription') ? ['Unsubscription'] : []),
        ...(f.includes('bounces') && channel === 'email' ? ['Bounces'] : []),
        ...(f.includes('clicks') ? ['Clicks'] : []),
        ...(f.includes('sendings') ? ['Sendings'] : []),
        ...(f.includes('opens') && channel === 'email' ? ['Opens'] : [])
      ] as ActivityEmailEventType[]
    }
})
*/

export const fetchRecipientTrackingChangeLogs = async (
  idRecipient: number,
  qsParams: GetEmailRecipientTrackingChangeLogsQsParams,
): Promise<Partial<RecipientTrackingChangeLogsState>> => {
  const idList: number = storeGet(STORE_KEY_ID_LIST);

  const response = await beRecipientApiGetEmailRecipientTrackingChangeLogs(
    idRecipient,
    idList,
    qsParams,
  );

  return {
    items: response.Items.map((item) => ({
      IdLog: item.IdLog,
      InsertDate:
        toDateUI(item.InsertDate, "YYYY-MM-DD HH:mm") ?? item.InsertDate,
      OpenTrackingEnabled: item.OpenTrackingEnabled,
      IdNLFrom: item.IdNLFrom,
    })),
    totalItems: response.TotalItems,
    pageNumber: response.PageNumber,
    pageSize: response.PageSize,
  };
};
